define('viewModels/privileged/composer/forms/formEditor/Evaluation',[
    'lib/knockout',
    'lib/lodash4',
    'services/composer/qualityService',
    'models/composer/forms/QuestionType',
    'models/composer/forms/qualityScore/EvaluationScore',
    'util/errorCallbackFactory',
    'viewModels/ViewModel',
    'viewModels/privileged/composer/forms/qualityScore/EvaluationScore',
    'viewModels/privileged/composer/forms/formEditor/QuestionGroup',
    'viewModels/privileged/composer/forms/formEditor/AddQuestionButtons',
    'viewModels/privileged/composer/forms/formEditor/FormEditorState',
    'viewModels/privileged/composer/vmUtils',
    'viewModels/privileged/composer/EntitySubviewMixinProvider',
    'services/toastService',
    'services/loggingService'
], function (
    ko,
    _,
    qualityService,
    QuestionType,
    EvaluationScoreModel,
    errorCallbackFactory,
    ViewModel,
    EvaluationScoreViewModel,
    QuestionGroupViewModel,
    AddQuestionButtonsViewModel,
    FormEditorState,
    utils,
    EntitySubviewMixinProvider,
    toastService,
    loggingService
) {
    "use strict";

    var EvaluationViewModel = ViewModel.extend(function evaluationVm_init(evaluationModel, namesList, formEditorState) {
        var self = this;

        // Returns the underlying model; used by the editor to save
        self.getModel = function () {
            return evaluationModel;
        };

        // Does not include question answers or comments
        self.getUnwrappedComposerModel = function () {
            return evaluationModel.unwrap({
                'evaluation': ['published', 'selfUri', 'comments'],
                'questionGroup': ['naSelected'],
                'baseQuestion': ['comments'],
                'multipleChoiceQuestion': ['answer', 'answerId'],
                'multipleChoiceQuestionAnswer': []
            }, { ignoreFakeIds: true });
        };

        // Includes question answers and comments
        self.getUnwrappedQualityManagementModel = function () {
            return evaluationModel.unwrap({
                'evaluation': ['notes', 'organizationId', 'version', 'modifiedDate', 'selfUri'],
                'questionGroup': [],
                'baseQuestion': ['naSelected', 'answer', 'comments'],
                'multipleChoiceQuestion': [],
                'multipleChoiceQuestionAnswer': []
            });
        };

        // The question groups associated with this evaluation
        self.questionGroups = utils.subviewList(self, evaluationModel.questionGroups, QuestionGroupViewModel, addQuestionGroupFromQuestionGroup, formEditorState, evaluationModel.questionMap); //addQuestionGroupFromQuestionGroup is defined below, hoisting lets me refer to it from here

        var entitySubviewMixinProvider = new EntitySubviewMixinProvider(self, "QuestionGroup", evaluationModel.questionGroups, self.questionGroups, {modifiedState: formEditorState.isModified, handleSelection: true});

        //self.moveQuestionGroup(start, end);
        //self.triggerMoveQuestionGroupUp
        //self.triggerMoveQuestionGroupDown
        entitySubviewMixinProvider.addReorderMethods();

        //self.triggerDeleteQuestionGroup
        //self.deleteQuestionGroup(index)
        entitySubviewMixinProvider.addDeleteMethods();


        //Adds isPreviewMode, isEditMode, isEvaluatorActionMode, etc.
        _.extend(self, formEditorState.modes);
        self.selectedItem = formEditorState.selectedItem;

        //Selects the default answer for each question
        self.selectDefaultAnswers = function() {
            _.invokeMap(self.questionGroups(), "selectDefaultAnswers");
        };

        // Flag to determine if the evaluation has been published
        self.isPublished = self.computed(function evaluationVm_isPublishedRead() {
            return ko.unwrap(evaluationModel.published);
        });

        // The name of the evaluation
        self.name = formEditorState.trackModified(self, evaluationModel.name);

        // Observable containing an evaluation's overall comments
        self.comments = evaluationModel.comments;

        //Computed that contains any validation errors defined based on model validations
        self.nameModelErrors = utils.modelErrors(self, evaluationModel.name);

        self.hasDuplicateName = self.computed(function() {
            return !self.isPublished() && namesList && _.find(namesList(), function (nameIdPair) {
                return nameIdPair.name.toUpperCase() === self.name().toUpperCase() && nameIdPair.id !== evaluationModel.id;
            });
        });

        //Checks for unique name in addition to any errors returned by the model
        self.nameErrors = self.computed(function () {
            var errors = [].concat(self.nameModelErrors());
            //Check if name is unique

            if(self.hasDuplicateName()) {
                errors.push(ko.i18n.renderString("composer.evaluations.editor.evaluation.duplicateNameError"));
            }
            return errors;
        });

        // in order to know when a user edits a question group's weight, we must subscribe to each question group
        var questionGroupSubscriptions = {}; // keep track of existing subscriptions (so we can unsubscribe later)
        function subscribeToQuestionGroup(questionGroup) {
            // subscribe to manualWeight for ALL question groups
            self.subscribe(questionGroup.manualWeight, function() {
                // only proceed if the weight was changed manually (so automatic updates don't have a cascading effect)
                if(questionGroup.manualWeight()) {
                    // subscribe to weight for user-set weights ONLY
                    questionGroupSubscriptions[questionGroup.id] = self.subscribe(questionGroup.weight, function() {
                        self.updateTotalWeight(); // when the user changes a weight, update the total weight and automatic weights
                    });
                } else {
                    // manualWeight was set to false, so the user just cleared the weight they set and want it to go back to automatic weighting
                    // unsubscribe from weight
                    var subscription = questionGroupSubscriptions[questionGroup.id];
                    if(subscription) {
                        subscription.dispose();
                        delete questionGroupSubscriptions[questionGroup.id];
                        self.updateTotalWeight();
                    }
                }
            });
            if(questionGroup.manualWeight()) {
                questionGroup.manualWeight.notifySubscribers(true);
            }
        }

        // subscribe to each question group to listen for manual changes to the weight
        _.forEach(self.questionGroups(), function(questionGroup) {
            subscribeToQuestionGroup(questionGroup);
        });

        // subscribe to the list of question groups to keep track of when a question group is added or removed
        var prevNumQuestions = 0;
        self.subscribe(self.questionGroups, function(oldValue) {
            prevNumQuestions = oldValue.length; // record the number of question groups BEFORE the change
        }, this, "beforeChange");

        self.subscribe(self.questionGroups, function(newValue) {
            // compare the number of question groups BEFORE the change to the number AFTER the change
            if(newValue.length != prevNumQuestions) {
                self.updateTotalWeight(); // if the number of question groups changed, recalculate the total and automatic weights
            }
        });

        // make sure the total weight of the evaluation is equal to 100%
        self.totalWeight = ko.observable(0);
        self.updateTotalWeight = function() {
            var totalWeight = 0.0;
            var unchangedQuestionGroups = []; // array of question groups whose weights have been set automatically
            // sort through the question groups to get the ones that have automatically set weights
            _.forEach(self.questionGroups(), function(questionGroup) {
                if(questionGroup.manualWeight()) {
                    totalWeight += questionGroup.weight();
                } else {
                    unchangedQuestionGroups.push(questionGroup); // keep track of the weights that need to be set automatically
                }
            });
            // calcuate the new weight for the automatically set weights and update them
            if(unchangedQuestionGroups.length > 0) { // avoid divide by zero
                // automatic weight formula: (100 - (sum of user-set weights))/(number of questions that need to be set automatically)
                var newWeight = Math.round(((100 - totalWeight) / unchangedQuestionGroups.length)*100) / 100;
                _.forEach(unchangedQuestionGroups, function(questionGroup, index) {
                    if(newWeight >= 0 && newWeight <= 100) { // make sure the calcuated weight is valid
                        // on the last one, subtract from 100% in case there were rounding errors
                        if(index == unchangedQuestionGroups.length - 1) {
                            newWeight = Math.round((100 - totalWeight)*100) / 100;
                        }
                        questionGroup.formattedWeight(newWeight);
                    }
                    totalWeight += questionGroup.weight();
                });
            }
            // if the total weight isn't a number, make it a dash
            if(isNaN(totalWeight)) {
                self.totalWeight("-");
            } else {
                self.totalWeight(Math.round(totalWeight*100)/100); // update the total weight
            }
        };
        self.updateTotalWeight();

        self.isValidForSaving = self.computed(function () {
            return evaluationModel.isValid() && !self.hasDuplicateName() && self.totalWeight() === 100;
        });

        //When isPreviewMode changes, call self.onPreviewModeChange
        var previewModeSubscription = self.isPreviewMode.subscribe(_.bindKey(self, "onPreviewModeChange"));

        self.onPreviewModeChange = function basePreviewModeChange(previewMode) {
            if(!previewMode) {
                self.comments('');
                self.submitValidationsFailed(false);
                formEditorState.shouldShowSubmitErrors(false);
            }
        };

        // Triggered by clicking on the Add Question Group button
        self.addQuestionGroup = function (data, index) {
            var newQuestionGroup = evaluationModel.addQuestionGroup(data, index);
            formEditorState.isModified(true);
            subscribeToQuestionGroup(newQuestionGroup);
        };

        /*jslint latedef:false*/ //I really don't want to put this logic before the subview list logic;

        //callback for adding from a sibiling; takes the data of the question group to add, the viewModel triggering the add, and the desired direction to add it in.
        function addQuestionGroupFromQuestionGroup(data, addingQuestionGroupViewModel, direction) {
            var questionIndex = _.indexOf(self.questionGroups(), addingQuestionGroupViewModel);

            if(questionIndex === -1) {
                loggingService.error("Couldn't find index of addingQuestionGroupViewModel in evaluation");
                return;
            }
            if(direction !== addQuestionGroupFromQuestionGroup.ABOVE && direction !== addQuestionGroupFromQuestionGroup.BELOW) {
                loggingService.error("Called with invalid direction: "+direction);
                return;
            }

            var newIndex = (direction === addQuestionGroupFromQuestionGroup.ABOVE ? questionIndex : questionIndex + 1);

            self.addQuestionGroup(data, newIndex);
        }
        addQuestionGroupFromQuestionGroup.ABOVE = "ABOVE";
        addQuestionGroupFromQuestionGroup.BELOW = "BELOW";


        self.addQuestionView = self.subView(AddQuestionButtonsViewModel, formEditorState);

        // Sends and evaluation to QM scoring service
        var scoringRaceConditionCounter = 0;
        self.scoreEvaluation = function () {
            // Call to QM Scoring Service
            scoringRaceConditionCounter++;
            var counterPriorToThisRequest = scoringRaceConditionCounter;
            var unwrappedQualityManagementModel = self.getUnwrappedQualityManagementModel();
            qualityService.post.scoreEvaluation(unwrappedQualityManagementModel)
            .then(function (jsObject) {
                if (scoringRaceConditionCounter === counterPriorToThisRequest) {
                    // get the question group name from the evaluation to put in live scoring dialog
                    _.forEach(jsObject.questionGroupScores, function(questionGroupScore, index) {
                        questionGroupScore.groupName = unwrappedQualityManagementModel.questionGroups[index].name;
                    });
                    self.setEvaluationScore(new EvaluationScoreModel(jsObject));
                    scoringRaceConditionCounter = 0;
                }
            });
        };

        // Triggered by evaluation Preview Submit Evaluation button (in preview mode)
        self.previewSubmitEvaluation = function () {
            var errors = [];

            errors = errors.concat(self.validationsForSubmission());
            // errors = errors.concat(self.nextValidator());

            if (errors.length === 0) { // if all submission validations are successful, notify success
                toastService.notifyMessage(
                    'composer.evaluations.editor.submitValidations.preview.success.title',
                    {
                        bodyMessageKey: 'composer.evaluations.editor.submitValidations.preview.success.message',
                        icon: "fa-check-circle"
                    }
                );
                self.scoreEvaluation();
            } else {
                formEditorState.shouldShowSubmitErrors(true);
                errorCallbackFactory.createCallback({namespace: 'composer.evaluations.editor.submitValidations.errors'})(errors);
            }
        };

        self.submitValidationsFailed = ko.observable(false);
        // Validates that all questions do indeed have answers.
        //   returns an array of validation failure messages
        self.validationsForSubmission = function () {
            var errors = [];
            _.each(self.questionGroups(), function (questionGroup) {
                // Only check the questions if the question group is applicable
                if (!questionGroup.naSelected() && questionGroup.visibilityConditionVisible()) {
                    _.each(questionGroup.questions(), function (question) {
                        errors = errors.concat(question.validationsForSubmission());
                    });
                }
            });
            if (errors.length) {
                self.submitValidationsFailed(true);
            }
            return errors;
        };

        // Live Scoring Handling
        //   This computed intentionally does not have an variable assigned.  It is a behavior
        //   that we want to occur when formEditorState.hasScoreChanged state changes.
        self.computed(function () {
            if (formEditorState.hasScoreChanged() && self.isPreviewMode()) {
                self.scoreEvaluation();
                formEditorState.hasScoreChanged(false);
            }
        });

        //Attached to keydown event to prevent enter key on contenteditable
        self.preventEnterKey = utils.preventEnterKey;

        // Start the score summary window expanded by default during agent or finished
        var startExpanded = formEditorState.modes.isAgentReviewMode() || formEditorState.modes.isFinishedReviewMode();
        var evaluationScoreShowingDetails = ko.observable(startExpanded);
        // Quality Score Result - Evaluation Score View Model
        self.evaluationScore = ko.observable(null);
        self.setEvaluationScore = function (model) {
            if (model !== null) {
                // format score so we use , when . is not appropriate
                model.totalScore(ko.i18n.format(model.totalScore(), 'n2'));
                model.totalCriticalScore(ko.i18n.format(model.totalCriticalScore(), 'n2'));
                var evaluationScoreViewModel = self.subView(EvaluationScoreViewModel, model, evaluationScoreShowingDetails, formEditorState);
                if (self.evaluationScore()) {
                    self.disposeSubView(self.evaluationScore());
                }
                self.evaluationScore(evaluationScoreViewModel);
            } else {
                self.evaluationScore(null);
            }
            //self.isLoading(false);
        };
        self.setEvaluationScore(new EvaluationScoreModel());

        // Get the answers in a form that quality can use them.  Transform attribute names
        self.getQualityAnswerData = function() {
            // separate the evaluation form (questions) from the answers
            // iterate over question groups
            var questionGroupScores = _.map(self.questionGroups(), function (questionGroup) {
                return questionGroup.getQualityAnswerData();
            });

            return {
                comments: evaluationModel.comments(),
                questionGroupScores: questionGroupScores
            };

        };

        //Fills in evaluation answer values from the quality service with provided answer data
        //    Doesn't need data for every question or question group, but logs an error if
        //    data is provided for a question or question group that doesn't exist
        self.fillQualityAnswerData = function (qualityAnswerData) {
            if (qualityAnswerData) {
                self.comments(qualityAnswerData.comments);
                //Fill out question group answer data
                _.each(qualityAnswerData.questionGroupScores, function (qualityQuestionGroupAnswerData) {
                    var id = qualityQuestionGroupAnswerData.questionGroupId;
                    //Find the corresponding question group
                    var questionGroupViewModel = _.find(self.questionGroups(), {id: id});
                    if (!questionGroupViewModel) {
                        loggingService.error("Provided answer data for non-existent question group", { id: id });
                        return;
                    }
                    questionGroupViewModel.fillQualityAnswerData(qualityQuestionGroupAnswerData);
                });
                self.setEvaluationScore(new EvaluationScoreModel(qualityAnswerData));
            }
        };

        // disposes subscription to isPreviewMode
        self.onDispose = function () {
            previewModeSubscription.dispose();
        };

    });
    return EvaluationViewModel;
});

